import type { ReactNode } from 'react'
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { usePreference, setPreference } from '@fs/zion-preferences'
import updateObjectPreference from './updateObjectPreference'

export type ProviderValue<T> = [T, (T) => void]

type PreferenceProviderProps<T> = {
  preferenceKey: string
  Context: React.Context<ProviderValue<T>>
  children: ReactNode
}

/**
 * This wrapping component lets us gate the actual provider from being rendered until we fetch
 * the preference. This way we don't have to manage a bunch of "could be undefined" scenarios for
 * the provider value.
 */
export default function PreferenceProvider<T>(
  props: PreferenceProviderProps<T> & { defaultValue?: T }
): JSX.Element | null {
  const { preferenceKey, defaultValue } = props
  const preferenceValue = usePreference<T>(preferenceKey)
  // In the case the user's preference isn't set, update it to the default preference.
  useEffect(() => {
    if (defaultValue && preferenceValue === '') {
      setPreference(preferenceKey, defaultValue)
    }

    updateObjectPreference(defaultValue, preferenceValue, preferenceKey)
  }, [defaultValue, preferenceKey, preferenceValue])

  const gatingValue = preferenceValue === '' ? defaultValue : preferenceValue

  return gatingValue ? <InternalPreferenceProvider {...props} preferenceValue={gatingValue} /> : null
}

function InternalPreferenceProvider<T>({
  preferenceKey,
  preferenceValue,
  Context,
  children,
}: PreferenceProviderProps<T> & { preferenceValue: T }): JSX.Element {
  const [pref, setPref] = useState<T>(preferenceValue)

  const updatePref = useCallback(
    (newValue: T): void => {
      setPref(newValue)
      setPreference(preferenceKey, newValue)
    },
    [preferenceKey]
  )

  const value: ProviderValue<T> = useMemo(() => [pref, updatePref], [pref, updatePref])

  return <Context.Provider value={value}>{children}</Context.Provider>
}
